import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { useState } from 'react'
import { authClient } from '../../auth/client'
import { Chalkboard } from '../../components/Chalkboard'
import { SiteFooter } from '../../components/SiteFooter'
import { SiteHeader } from '../../components/SiteHeader'
import { DisplayNameForm } from '../../components/account/DisplayNameForm'
import { useLocale } from '../../components/use-locale'
import { getDict, parseLocale } from '../../i18n'
import { seoHead } from '../../lib/seo'
import { SITE_ORIGIN } from '../../lib/site'

export const Route = createFileRoute('/{-$locale}/me')({
  beforeLoad: ({ params }) => {
    if (!parseLocale(params.locale)) throw notFound()
  },
  head: ({ params }) => {
    const locale = parseLocale(params.locale) ?? 'ja'
    const t = getDict(locale)
    return seoHead({
      origin: SITE_ORIGIN,
      locale,
      path: '/me',
      title: t.meta.meTitle,
      description: t.account.nameHint,
      noindex: true,
    })
  },
  component: MePage,
})

type MeUser = {
  id: string
  displayName?: string | null
  handle?: string | null
  isAnonymous?: boolean | null
}

/** マイページ: 表示名の変更と、公開プロフィールへの入口 */
function MePage() {
  const { t, path } = useLocale()
  const session = authClient.useSession()
  const user = session.data?.user as MeUser | undefined
  const [saved, setSaved] = useState(false)

  let body: React.ReactNode
  if (session.isPending) body = <div className="account__placeholder" aria-hidden="true" />
  else if (!user)
    body = (
      <>
        <p className="lp-body">{t.account.signedOut}</p>
        <Link to={path('/cafe')} className="btn btn--lamp">
          {t.nav.enter}
        </Link>
      </>
    )
  else
    body = (
      <>
        <Chalkboard title={t.account.editName} titleId="me-name" className="board--paper account__board">
          <DisplayNameForm
            // 保存のたびに入力欄を作り直して、保存済みの値を初期値にする
            key={user.displayName ?? ''}
            initial={user.displayName ?? ''}
            submitLabel={user.displayName ? t.account.save : t.account.nameSubmit}
            onSaved={() => {
              setSaved(true)
              void session.refetch()
            }}
          />
          {saved && (
            <p className="account__saved" role="status">
              {t.account.saved}
            </p>
          )}
        </Chalkboard>
        {user.isAnonymous ? (
          <p className="account__note">{t.account.guestNote}</p>
        ) : (
          user.handle && (
            <Link to={path(`/u/${user.handle}`)} className="account__link">
              {t.account.publicProfile}
            </Link>
          )
        )}
        <Link to={path('/notifications')} className="account__link">
          {t.nav.notifications}
        </Link>
        <Link to={path('/cafe')} className="account__link">
          {t.account.backToCafe}
        </Link>
      </>
    )

  return (
    <>
      <SiteHeader variant="solid" />
      <main id="main" className="account">
        <h1 className="legal__title">{t.account.title}</h1>
        {body}
      </main>
      <SiteFooter />
    </>
  )
}
